import {Injectable} from '@angular/core';
import merge from 'lodash-es/merge';

const ENCRYPTION_KEY = base64toByteArray('+SX+kS4g2wxLhR5CHo8iM7xFnGsh8JgF++ldehfueFA=');

@Injectable({
    providedIn: 'root',
})
export class AppConfigService {
    static appConfig: AppConfig;
    static serverTimeDiff: number = 0;

    private frontendConfigUrl = 'assets/config/app-config.json';
    private backendConfigUrl = '/api/frontend/v1/config';

    static config<T>(): T {
        return AppConfigService.appConfig as T;
    }

    init(initialConfig: AppConfig): Promise<void> {
        return Promise.all([
            this.fetchBackendConfig(initialConfig.enableBackendConfig, this.backendConfigUrl),
            this.fetchFrontendConfig(initialConfig.enableFrontendConfig),
        ])
            .then(([backendConfig, frontendConfig]) =>
                AppConfigService.appConfig = merge(initialConfig, backendConfig, frontendConfig),
            )
            .then(() => {
                (window as any).angularBundlesAreReady = true;
                (window as any).appConfig = AppConfigService.appConfig;
            });
    }

    private fetchBackendConfig(enabled: boolean, url: string): Promise<any> {
        return enabled
            ? fetch(url)
                .then(response => response.json())
                .then(response => {
                    const serverTimeDiff = new Date().getTime() - new Date(response.creationTime).getTime();
                    if (Math.abs(serverTimeDiff) > 60_000) {
                        console.error(`Significant time difference for client and server time has been detected: ${serverTimeDiff}ms`);
                        AppConfigService.serverTimeDiff = serverTimeDiff;
                    }
                    AppConfigService.serverTimeDiff = serverTimeDiff > 60_000 ? serverTimeDiff : 0;
                    return !!response.iv
                        ? this.decryptAes(response.data, response.iv).then(result => JSON.parse(result))
                        : response.data;
                })
            : Promise.resolve({});
    }

    private fetchFrontendConfig(enabled: boolean): Promise<any> {
        return enabled
            ? fetch(this.frontendConfigUrl).then(response => response.json())
            : Promise.resolve({});
    }

    private decryptAes(data: string, iv: string): Promise<any> {
        return crypto.subtle.importKey(
            'raw',
            ENCRYPTION_KEY,
            {name: 'AES-GCM'},
            false,
            ['decrypt'],
        ).then(cryptoKey =>
            crypto.subtle.decrypt(
                {name: 'AES-GCM', iv: base64toByteArray(iv)},
                cryptoKey,
                base64toByteArray(data),
            ).then(decryptedBuffer => new TextDecoder().decode(decryptedBuffer)),
        );
    }
}

export interface AppConfig {
    [property: string]: any;

    frontendVersion?: string;
    enableFrontendConfig?: boolean;
    enableBackendConfig?: boolean;
}

function base64toByteArray(base64: string): Uint8Array<ArrayBuffer> {
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
}
